import { useEffect, useMemo, useState } from "react";
import { ClassSubjectsData } from "../types";
import { fetchClassSubjects, saveClassSubjects } from "../services/classSubjectsService";

const sameCodes = (a: string[], b: string[]) => a.length === b.length && a.every((code) => b.includes(code));

/**
 * Class ↔ subject ticks, keyed by gradeSectionId. Changes stay in `draft` until `saveAll`, which
 * sends one request per changed class; `saved` is the last state the server confirmed.
 */
export const useClassSubjects = () => {
  const [data, setData] = useState<ClassSubjectsData>({ subjects: [], grades: [] });
  const [saved, setSaved] = useState<Record<number, string[]>>({});
  const [draft, setDraft] = useState<Record<number, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetchClassSubjects();
        const byClass: Record<number, string[]> = {};
        response.grades.forEach((grade) =>
          grade.sections.forEach((section) => {
            byClass[section.gradeSectionId] = section.subjectCodes;
          })
        );
        setData(response);
        setSaved(byClass);
        setDraft(byClass);
      } catch (err) {
        setError("Failed to load class subjects.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const changedIds = useMemo(
    () =>
      Object.keys(draft)
        .map(Number)
        .filter((id) => !sameCodes(draft[id], saved[id] ?? [])),
    [draft, saved]
  );

  const toggle = (gradeSectionId: number, subjectCode: string) => {
    setDraft((prev) => {
      const current = prev[gradeSectionId] ?? [];
      const next = current.includes(subjectCode)
        ? current.filter((code) => code !== subjectCode)
        : [...current, subjectCode];
      return { ...prev, [gradeSectionId]: next };
    });
  };

  /** Copies one class's ticks to every other section of the same grade. */
  const copyToGrade = (fromGradeSectionId: number, gradeId: number) => {
    const grade = data.grades.find((g) => g.id === gradeId);
    if (!grade) return;
    setDraft((prev) => {
      const next = { ...prev };
      grade.sections
        .filter((section) => section.gradeSectionId !== fromGradeSectionId)
        .forEach((section) => {
          next[section.gradeSectionId] = [...(prev[fromGradeSectionId] ?? [])];
        });
      return next;
    });
  };

  const discard = () => setDraft(saved);

  /** Saves every changed class; returns how many failed (0 = all saved). */
  const saveAll = async () => {
    setSaving(true);
    setError(null);
    let failed = 0;
    for (const gradeSectionId of changedIds) {
      try {
        const updated = await saveClassSubjects(gradeSectionId, draft[gradeSectionId]);
        setSaved((prev) => ({ ...prev, [gradeSectionId]: updated.subjectCodes }));
        setDraft((prev) => ({ ...prev, [gradeSectionId]: updated.subjectCodes }));
      } catch (err) {
        failed++;
      }
    }
    if (failed > 0) setError(`Failed to save ${failed} class(es). Their changes are still on screen.`);
    setSaving(false);
    return failed;
  };

  return {
    subjects: data.subjects,
    grades: data.grades,
    draft,
    changedIds,
    loading,
    saving,
    error,
    actions: {
      toggle,
      copyToGrade,
      discard,
      saveAll,
    },
  };
};
